import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { siteConfig, whatsappUrl } from "../../config/siteConfig";
import { WhatsAppIcon } from "./icons";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const INITIAL_FORM = {
    name: "",
    company: "",
    email: "",
    whatsapp: "",
    site_type: "institucional",
    description: "",
};

const inputClass =
    "w-full px-4 py-3 rounded-lg bg-[#0a0d14] border border-[#222e42] text-white text-sm focus:border-blue-500 focus:outline-none transition-colors placeholder:text-slate-600";

const labelClass = "block text-xs uppercase tracking-wider text-slate-300 font-semibold mb-2";

export const QuoteForm = () => {
    const [form, setForm] = useState(INITIAL_FORM);
    const [errors, setErrors] = useState({});
    const [status, setStatus] = useState("idle"); // idle | sending | success | error
    const [serverError, setServerError] = useState("");

    const setField = (field) => (e) => {
        setForm((f) => ({ ...f, [field]: e.target.value }));
        setErrors((err) => ({ ...err, [field]: undefined }));
    };

    const validate = () => {
        const err = {};
        if (form.name.trim().length < 2) err.name = "Informe seu nome completo.";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
            err.email = "Informe um e-mail válido.";
        if (form.whatsapp.replace(/\D/g, "").length < 10)
            err.whatsapp = "Informe um WhatsApp válido com DDD.";
        if (form.description.trim().length < 10)
            err.description = "Descreva brevemente o seu projeto (mínimo 10 caracteres).";
        return err;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const err = validate();
        if (Object.keys(err).length > 0) {
            setErrors(err);
            return;
        }
        setStatus("sending");
        setServerError("");
        try {
            const res = await fetch(`${API_URL}/api/quotes`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: form.name.trim(),
                    company: form.company.trim(),
                    email: form.email.trim(),
                    whatsapp: form.whatsapp.trim(),
                    site_type: form.site_type,
                    description: form.description.trim(),
                }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(
                    typeof data.detail === "string"
                        ? data.detail
                        : "Não foi possível enviar seu pedido. Tente novamente."
                );
            }
            setStatus("success");
        } catch (error) {
            setStatus("error");
            setServerError(error.message);
        }
    };

    return (
        <section
            id="orcamento"
            data-testid="quote-section"
            className="py-24 border-b border-[#1e2430] bg-[#0c1017]"
        >
            <div className="max-w-4xl mx-auto px-6">
                <div className="text-center mb-10">
                    <span className="code-font text-xs text-[#2563eb] uppercase tracking-wider block mb-2">
                        Contato &amp; Orçamento
                    </span>
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-4">
                        Seu próximo projeto digital começa aqui.
                    </h2>
                    <p className="text-slate-400 text-sm max-w-xl mx-auto mb-8">
                        Conte um pouco sobre sua empresa e descubra como podemos ajudar sua marca a
                        se destacar online.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <a
                            href="#quote-form"
                            data-testid="cta-solicitar-orcamento"
                            className="px-6 py-3 bg-[#2563eb] hover:bg-[#1d4ed8] text-white font-medium text-sm rounded-lg transition-colors shadow-lg shadow-blue-600/30"
                        >
                            Solicitar orçamento
                        </a>
                        <a
                            href={whatsappUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="cta-falar-com-linkora"
                            className="px-6 py-3 bg-[#131822] hover:bg-[#1b2230] border border-[#263142] text-slate-200 font-medium text-sm rounded-lg transition-colors flex items-center gap-2"
                        >
                            <WhatsAppIcon className="w-4 h-4" />
                            Falar pelo WhatsApp
                        </a>
                    </div>
                </div>

                <div
                    id="quote-form"
                    className="p-8 md:p-10 rounded-2xl bg-[#111724] border border-[#222e42] shadow-2xl"
                >
                    {status === "success" ? (
                        <div data-testid="quote-success" className="text-center py-10">
                            <CheckCircle2 className="w-12 h-12 text-blue-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-white mb-2">
                                Pedido enviado com sucesso.
                            </h3>
                            <p className="text-sm text-slate-400 max-w-md mx-auto mb-6">
                                Recebemos suas informações e retornaremos pelo e-mail ou WhatsApp
                                informado. Obrigado pelo contato.
                            </p>
                            <button
                                type="button"
                                data-testid="quote-send-another"
                                onClick={() => {
                                    setForm(INITIAL_FORM);
                                    setStatus("idle");
                                }}
                                className="px-6 py-3 bg-[#131822] hover:bg-[#1b2230] border border-[#263142] text-slate-200 text-sm rounded-lg transition-colors"
                            >
                                Enviar outro pedido
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} noValidate className="space-y-6" data-testid="quote-form">
                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="quote-name" className={labelClass}>
                                        Nome Completo *
                                    </label>
                                    <input
                                        id="quote-name"
                                        type="text"
                                        value={form.name}
                                        onChange={setField("name")}
                                        placeholder="Seu nome"
                                        data-testid="quote-input-name"
                                        className={inputClass}
                                    />
                                    {errors.name && (
                                        <p data-testid="quote-error-name" className="text-xs text-red-400 mt-1.5">
                                            {errors.name}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="quote-company" className={labelClass}>
                                        Nome da Empresa
                                    </label>
                                    <input
                                        id="quote-company"
                                        type="text"
                                        value={form.company}
                                        onChange={setField("company")}
                                        placeholder="Nome do seu negócio ou clínica"
                                        data-testid="quote-input-company"
                                        className={inputClass}
                                    />
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-6">
                                <div>
                                    <label htmlFor="quote-email" className={labelClass}>
                                        E-mail *
                                    </label>
                                    <input
                                        id="quote-email"
                                        type="email"
                                        value={form.email}
                                        onChange={setField("email")}
                                        placeholder="seu@email.com"
                                        data-testid="quote-input-email"
                                        className={inputClass}
                                    />
                                    {errors.email && (
                                        <p data-testid="quote-error-email" className="text-xs text-red-400 mt-1.5">
                                            {errors.email}
                                        </p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="quote-whatsapp" className={labelClass}>
                                        WhatsApp *
                                    </label>
                                    <input
                                        id="quote-whatsapp"
                                        type="tel"
                                        value={form.whatsapp}
                                        onChange={setField("whatsapp")}
                                        placeholder="(DDD) 99999-9999"
                                        data-testid="quote-input-whatsapp"
                                        className={inputClass}
                                    />
                                    {errors.whatsapp && (
                                        <p data-testid="quote-error-whatsapp" className="text-xs text-red-400 mt-1.5">
                                            {errors.whatsapp}
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="quote-site-type" className={labelClass}>
                                    Tipo de Site Desejado *
                                </label>
                                <select
                                    id="quote-site-type"
                                    value={form.site_type}
                                    onChange={setField("site_type")}
                                    data-testid="quote-select-site-type"
                                    className={inputClass}
                                >
                                    <option value="institucional">Site Institucional</option>
                                    <option value="landing_page">Landing Page</option>
                                    <option value="local">Site para Negócio Local / Clínica</option>
                                    <option value="redesign">Redesign de Site Existente</option>
                                </select>
                            </div>

                            <div>
                                <label htmlFor="quote-description" className={labelClass}>
                                    Descrição do Projeto *
                                </label>
                                <textarea
                                    id="quote-description"
                                    rows={4}
                                    value={form.description}
                                    onChange={setField("description")}
                                    placeholder="Conte brevemente sobre o seu negócio e os objetivos com o novo site..."
                                    data-testid="quote-input-description"
                                    className={inputClass}
                                />
                                {errors.description && (
                                    <p data-testid="quote-error-description" className="text-xs text-red-400 mt-1.5">
                                        {errors.description}
                                    </p>
                                )}
                            </div>

                            {status === "error" && (
                                <p data-testid="quote-server-error" className="text-xs text-red-400">
                                    {serverError}
                                </p>
                            )}

                            <div className="pt-2 flex flex-col sm:flex-row gap-4 items-center justify-between">
                                <p className="text-xs text-slate-400">
                                    Envio direto para:{" "}
                                    <span className="text-blue-300 font-mono">{siteConfig.email}</span>
                                </p>
                                <button
                                    type="submit"
                                    disabled={status === "sending"}
                                    data-testid="quote-submit-button"
                                    className="w-full sm:w-auto px-8 py-3.5 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg transition-colors shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2"
                                >
                                    {status === "sending" && <Loader2 className="w-4 h-4 animate-spin" />}
                                    {status === "sending" ? "Enviando..." : "Solicitar orçamento"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </section>
    );
};
